import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Loader2, Upload, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Plumbing", "Roofing", "Electrical", "Painting", "Carpentry", "Garden", "Security", "Structural", "General"];
const PRIORITIES = ["LOW", "NORMAL", "HIGH", "URGENT"];

export default function NewRequest() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({
    property_id: params.get("property") || "", category: "Plumbing", priority: "NORMAL", description: "",
  });
  const [photos, setPhotos] = useState([]); // {path, filename}
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    api.get("/properties").then((res) => {
      setProperties(res.data);
      if (!form.property_id && res.data[0]) set("property_id", res.data[0].id);
    });
  }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, { storage_path: data.storage_path, filename: data.filename, preview: URL.createObjectURL(file) }]);
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) { setError("Please select a property"); return; }
    setLoading(true);
    setError("");
    try {
      await api.post("/maintenance-requests", { ...form, photos: photos.map((p) => p.storage_path) });
      toast.success("Maintenance request submitted");
      nav("/requests");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl fade-up">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
      </button>
      <p className="overline text-accent">Request Maintenance</p>
      <h1 className="font-heading text-4xl font-bold tracking-tight mt-1 mb-8">New Request</h1>

      <Card className="p-8 border-border">
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label>Property</Label>
            <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
              <SelectTrigger data-testid="request-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.property_name} · {p.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="request-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="request-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Describe the issue</Label>
            <Textarea id="desc" required rows={4} value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="e.g. Water leak in the ceiling of the living room during rain." data-testid="request-description-input" />
          </div>
          <div className="space-y-2">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative h-20 w-20 rounded-lg border border-border bg-secondary overflow-hidden">
                  <img src={p.preview} alt={p.filename} className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="h-20 w-20 rounded-lg border border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:bg-secondary transition-colors" data-testid="upload-photo-label">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : <Upload className="h-5 w-5 text-muted-foreground" aria-hidden="true" />}
                <span className="text-[0.6rem] text-muted-foreground mt-1">Upload</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="photo-input" />
              </label>
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={loading || uploading} className="rounded-full hover:bg-primary/90" data-testid="submit-request-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit Request
          </Button>
        </form>
      </Card>
    </div>
  );
}
